import { useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { AxiosError } from "axios";
import { SearchIcon } from "@chakra-ui/icons";
import { FiExternalLink } from "react-icons/fi";
import {
  buildConsolidatedDataFileName,
  getBundleExplorerUrl,
  getLocalhostPreviewUrl,
} from "@metriport/shared-internal";
import { Tag } from "../../shared/Tag";
import useMetriportToast from "../../shared/toast";
import { getErrorMessageInternal } from "../../../shared/error";
import { CxIdByPatientIdResponse, getDocumentPresignedUrl } from "../../../api/patients";
import { capture } from "../../../shared/capture";

type CustomerIdLookupFormProps = {
  onLookup: (patientId: string) => Promise<CxIdByPatientIdResponse>;
};

export default function CustomerIdLookupForm({ onLookup }: CustomerIdLookupFormProps) {
  const toast = useMetriportToast();
  const [patientId, setPatientId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<CxIdByPatientIdResponse | undefined>(undefined);
  const [searched, setSearched] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState<"localhost" | "explorer" | null>(null);

  async function handleSearch() {
    const trimmed = patientId.trim();
    if (trimmed.length === 0) {
      toast.warning({ title: "Please enter a patient ID" });
      return;
    }

    try {
      setIsLoading(true);
      setSearched(false);
      setResult(undefined);

      const lookupResult = await onLookup(trimmed);
      setResult(lookupResult);
    } catch (err) {
      setResult(undefined);
      const is404 = err instanceof AxiosError && err.response?.status === 404;

      if (is404) return;
      const message = getErrorMessageInternal(err);
      toast.error({ title: "Failed to look up cxId", description: message });
    } finally {
      setSearched(true);
      setIsLoading(false);
    }
  }

  function handleOnChange(e: React.ChangeEvent<HTMLInputElement>) {
    setPatientId(e.target.value);
    setSearched(false);
    setResult(undefined);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && !isLoading) {
      handleSearch();
    }
  }

  async function openPreview(type: "localhost" | "explorer") {
    if (!result?.cxId || !patientId.trim()) return;
    setLoadingPreview(type);
    try {
      const fileName = buildConsolidatedDataFileName(result.cxId, patientId.trim());
      const { url } = await getDocumentPresignedUrl(result.cxId, fileName);
      const previewBaseUrl =
        type === "localhost" ? getLocalhostPreviewUrl() : getBundleExplorerUrl();
      const previewUrl = `${previewBaseUrl}?url=${encodeURIComponent(url)}`;
      window.open(previewUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      capture.error(error, { extra: { context: "customer-lookup.openPreview", type } });
      toast.error({ title: "Failed to get presigned URL" });
    } finally {
      setLoadingPreview(null);
    }
  }

  return (
    <>
      <Text fontWeight="bold" mb={2} fontSize="lg">
        Patient Explorer
      </Text>
      <Text color="gray.600" mb={4} fontSize="sm">
        Quickly identify which customer owns a patient and inspect their consolidated FHIR bundle.
      </Text>
      <FormControl mb={4}>
        <FormLabel>Patient ID</FormLabel>
        <Flex gap={2}>
          <Input
            value={patientId}
            onChange={handleOnChange}
            onKeyDown={handleKeyDown}
            placeholder="Enter patient ID"
            bg="white"
          />
          <Button
            colorScheme="blue"
            onClick={handleSearch}
            isDisabled={isLoading}
            leftIcon={isLoading ? undefined : <SearchIcon />}
          >
            {isLoading ? <Spinner size="sm" /> : "Search"}
          </Button>
        </Flex>
      </FormControl>
      {searched && (
        <Box mt={4} p={4} bg="gray.50" borderRadius="md">
          {result ? (
            <VStack align="start" spacing={3}>
              <Box>
                <Text fontWeight="semibold" mb={1} fontSize="sm" color="gray.600">
                  Customer ID:
                </Text>
                <Tag id="cxId" label={result.cxId} canCopy />
              </Box>
              {result.name && (
                <Box>
                  <Text fontWeight="semibold" mb={1} fontSize="sm" color="gray.600">
                    Name:
                  </Text>
                  <Text>{result.name}</Text>
                </Box>
              )}
              {result.location && Object.keys(result.location).length > 0 && (
                <Box>
                  <Text fontWeight="semibold" mb={1} fontSize="sm" color="gray.600">
                    Location:
                  </Text>
                  <Text fontFamily="mono" fontSize="sm">
                    {JSON.stringify(result.location, null, 2)}
                  </Text>
                </Box>
              )}
              <Box pt={2}>
                <Text fontWeight="semibold" mb={2} fontSize="sm" color="gray.600">
                  Patient Tools:
                </Text>
                <Flex gap={2} wrap="wrap">
                  <Button
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                    rightIcon={<FiExternalLink />}
                    onClick={() => openPreview("localhost")}
                    isLoading={loadingPreview === "localhost"}
                    isDisabled={loadingPreview !== null}
                  >
                    Open in Localhost Preview
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                    rightIcon={<FiExternalLink />}
                    onClick={() => openPreview("explorer")}
                    isLoading={loadingPreview === "explorer"}
                    isDisabled={loadingPreview !== null}
                  >
                    Open in Bundle Explorer
                  </Button>
                </Flex>
              </Box>
            </VStack>
          ) : (
            <Text color="gray.500">No customer ID found for this patient ID</Text>
          )}
        </Box>
      )}
    </>
  );
}
